import { Controller } from "@hotwired/stimulus"
import { NativeBridge } from "../lib/native_bridge"
import { replayAnimation } from "../lib/motion"

// Badge Sync Controller
// Ensures badge counts stay in sync through multiple mechanisms:
//
// 1. Custom Turbo Stream Action (primary - real-time)
//    - Listens for "badge:update" events dispatched by custom update_badge action
//    - Bypasses data-turbo-permanent restriction on bottom navigation
//    - Handles broadcasts from Badges::BroadcastService
//
// 2. Visibility Change Sync (fallback - app wake/resume)
//    - Fetches fresh badge counts when page becomes visible
//    - Handles WebSocket reconnection gaps after background/sleep
//
// 3. Per-visit sync (native apps only)
//    - <body> is replaced on every Turbo visit, so connect() runs once per page
//
// 4. Native Badge Sync (native apps only)
//    - Pushes badge counts to native tab bar via NativeBridge.TabBadge
//    - Listens for "native:badgeSync" event from iOS/Android lifecycle
//    - 300ms dedup window prevents duplicate bridge calls from dual-path broadcasts
//
// Problem: Action Cable WebSocket can disconnect when:
// - App goes to background (mobile/Android WebView)
// - Network changes (WiFi <-> Cellular)
// - Device sleeps and wakes
// - data-turbo-permanent prevents standard Turbo Stream updates
//
// Solution: Custom Turbo Stream action + JavaScript event bridge + native bridge
//
// Usage: data-controller="badge-sync" on <body> element
export default class extends Controller {
  static values = {
    refreshUrl: { type: String, default: "/api/native/badges" }
  }

  // Native tab key mapping: web badge targetId → { tabKey, style }
  // chat: "count" style (exact number matters for user urgency)
  // notification: "dot" style (just presence — Hick's Law: reduce cognitive load)
  static NATIVE_TAB_MAP = {
    "chat_badge": { tabKey: "chats", style: "count" },
    "notification_badge": { tabKey: "profile", style: "dot" }
  }

  // Dedup window for dual-path broadcasts (Turbo Stream + ActionCable)
  static DEDUP_WINDOW_MS = 300

  connect() {
    // 1. Listen for custom Turbo Stream badge:update events (real-time)
    this.boundHandleBadgeUpdate = this.handleBadgeUpdate.bind(this)
    window.addEventListener("badge:update", this.boundHandleBadgeUpdate)

    // 2. Visibility change sync (fallback for WebSocket gaps)
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.boundHandleVisibilityChange)

    // 4. Native badge sync event (from iOS sceneDidBecomeActive / Android onResume)
    this.boundHandleNativeSync = () => this.syncBadges()
    window.addEventListener("native:badgeSync", this.boundHandleNativeSync)

    // 3. One sync per visit (connect runs on every body swap)
    if (NativeBridge.isNative()) {
      this._lastApplied = {}
      this.syncBadges()
    }

    console.debug("[BadgeSync] Controller connected - listening for badge:update events")
  }

  disconnect() {
    window.removeEventListener("badge:update", this.boundHandleBadgeUpdate)
    document.removeEventListener("visibilitychange", this.boundHandleVisibilityChange)
    window.removeEventListener("native:badgeSync", this.boundHandleNativeSync)
    this._lastApplied = null
  }

  // Handle custom Turbo Stream badge:update events
  // Event dispatched by turbo_stream_actions/update_badge.js
  handleBadgeUpdate(event) {
    const { targetId, count } = event.detail

    if (!targetId) {
      console.warn("[BadgeSync] badge:update event missing targetId")
      return
    }

    this.applyBadge(targetId, count, { animate: true, haptic: true })
    console.debug(`[BadgeSync] Real-time update - ${targetId}: ${count}`)
  }

  handleVisibilityChange() {
    if (document.visibilityState === "visible") {
      // Page became visible - sync badges after a short delay
      // (allows WebSocket to potentially reconnect first)
      setTimeout(() => this.syncBadges(), 500)
    }
  }

  // /api/native/badges rejects non-native requests; coalesce concurrent calls.
  syncBadges() {
    if (!NativeBridge.isNative() || document.body.dataset.userSignedIn !== "true") return
    this._syncInFlight ||= this.fetchAndApplyBadges().finally(() => { this._syncInFlight = null })
    return this._syncInFlight
  }

  async fetchAndApplyBadges() {
    try {
      const response = await fetch(this.refreshUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })

      if (!response.ok) {
        console.debug("[BadgeSync] Failed to fetch badge counts:", response.status)
        return
      }

      const data = await response.json()

      // Response format from /api/native/badges:
      // { success: true, chat_unread: N, notifications_unread: N }
      if (!data.success) {
        console.debug("[BadgeSync] API returned error")
        return
      }

      // Update notification badge (no animation/haptic for sync — avoid jarring on resume)
      this.applyBadge("notification_badge", data.notifications_unread, { animate: false })

      // Update chat badge
      this.applyBadge("chat_badge", data.chat_unread, { animate: false })

      console.debug("[BadgeSync] HTTP sync complete:", data)
    } catch (error) {
      // Network error - ignore silently (WebSocket may still work)
      console.debug("[BadgeSync] HTTP sync failed:", error.message)
    }
  }

  // Single path for all badge updates: DOM + native
  // Deduplicates dual-path broadcasts within DEDUP_WINDOW_MS
  applyBadge(targetId, count, { animate = true, haptic = false } = {}) {
    // 1. Web DOM update (always, regardless of native)
    this._updateDOM(targetId, count)

    // 2. Native tab bar (native app only)
    if (!NativeBridge.isNative()) return

    const mapping = this.constructor.NATIVE_TAB_MAP[targetId]
    if (!mapping) return

    // 3. Dedup: same count within window → skip (handles Turbo Stream + ActionCable dual-path)
    this._lastApplied = this._lastApplied || {}
    const last = this._lastApplied[targetId]
    const now = Date.now()
    if (last && last.count === count && (now - last.ts) < this.constructor.DEDUP_WINDOW_MS) {
      return
    }
    const prevCount = last?.count
    this._lastApplied[targetId] = { count, ts: now }

    // 4. Push to native tab bar
    NativeBridge.TabBadge.set(mapping.tabKey, count, { animate, style: mapping.style })

    // 5. Haptic feedback on a real increment — the first count a page learns (e.g. at launch) is not news
    if (haptic && prevCount !== undefined && count > prevCount) {
      NativeBridge.Haptic.impact("light")
    }
  }

  // DOM-only badge update (extracted from original updateBadge)
  _updateDOM(targetId, count) {
    const badge = document.getElementById(targetId)
    if (!badge) {
      console.debug(`[BadgeSync] Target element not found: ${targetId}`)
      return
    }

    const maxDisplay = 99
    const previous = Number.parseInt(badge.textContent, 10) || 0

    if (count > 0) {
      const displayCount = count > maxDisplay ? `${maxDisplay}+` : count
      badge.textContent = displayCount
      badge.className = "absolute -top-1 -right-1 flex items-center justify-center min-w-[1.125rem] h-[1.125rem] px-1 text-2xs font-bold text-white bg-crust-500 rounded-full shadow-sm"
      badge.setAttribute("aria-label", `${count} unread`)
      if (count > previous) replayAnimation(badge, "badge-pop")
    } else {
      badge.textContent = ""
      badge.className = ""
      badge.removeAttribute("aria-label")
    }
  }
}
